import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import * as React from 'react';
import { createRef, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { FormFieldNames } from '../../validations/assignRfidCardValidationSchema';
import OutlinedTextField, { IOutlinedTextField } from '../HookFormFields/TextField';
import { TTextFieldAllowedChars } from '../HookFormFields/types';
import ErrorHelperText from './ErrorHelperText';
import { cardIdentifierMask } from './helpers/cardIdentifierMask';
import messages from './messages';

interface ICardIdentifierInput extends Omit<
  IOutlinedTextField<{ [FormFieldNames.Card]: string }>,
  'allowedChars' | 'disabled' | 'error' | 'helperText' | 'label' | 'mask' | 'name' | 'placeholder'
> {
  cardId: string;
  error: string;
  isConnecting: boolean;
  isDriverInitialized: boolean;
  isInitError: boolean;
  isReading: boolean;
  isTesting: boolean;
  fobType?: 'dormakaba' | 'standard';
  stateOnlyHelperText?: boolean;
  allowedChars?: TTextFieldAllowedChars;
}

const CardIdentifierInput = ({
  allowedChars,
  cardId,
  error,
  fobType = 'standard',
  isConnecting,
  isDriverInitialized,
  isInitError,
  isReading,
  isTesting,
  slotProps,
  stateOnlyHelperText,
  ...textFieldProps
}: ICardIdentifierInput) => {
  const { formatMessage } = useIntl();
  const inputRef: React.RefObject<HTMLInputElement | null> = createRef();
  const { clearErrors, formState, setValue, watch } = useFormContext();
  const { errors, isValid } = formState;
  const errorMessage = errors?.card?.message;
  const cardValue = watch(FormFieldNames.Card);
  const hasCardValue = !!cardIdentifierMask.parse(String(cardValue ?? ''));

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputRef]);

  useEffect(() => {
    if (cardId) {
      setValue(FormFieldNames.Card, cardId, { shouldValidate: true });
    }
  }, [cardId]);

  useEffect(() => {
    if (isReading || isConnecting) {
      clearErrors();
      setValue(FormFieldNames.Card, '');
    }
  }, [isReading, isConnecting]);

  useEffect(() => {
    if (!hasCardValue) {
      clearErrors(FormFieldNames.Card);
    }
  }, [clearErrors, hasCardValue]);

  const isError = !!error && !hasCardValue;
  const isInvalid = !!errors?.card && hasCardValue;
  const mask = fobType === 'dormakaba' ? cardIdentifierMask : undefined;

  const getHelperText = () => {
    if (fobType === 'standard') {
      if (isTesting || isConnecting) {
        return <FormattedMessage {...messages.cardIdentifierInputHelperTextDriverTesting} />;
      }

      if (isReading) {
        return (
        <Box component="span" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            <FormattedMessage {...messages.cardIdentifierInputErrorReading} />
          </Box>
        );
      }
    }

    if (isValid && cardId) {
      return (
        <Box component="span" sx={{ color: 'success.main' }}>
          <FormattedMessage {...messages.cardIdentifierInputErrorValidId} />
        </Box>
      );
    }

    if (fobType === 'standard' && !stateOnlyHelperText && (isInitError || (!isDriverInitialized && isValid))) {
      return <FormattedMessage {...messages.cardIdentifierInputHelperTextDefault} />;
    }

    if ((isError || isInvalid) && !isInitError) {
      return (
        <ErrorHelperText error={error} errorMessage={errorMessage as React.ReactNode} validationError={isInvalid} />
      );
    }

    if (!stateOnlyHelperText && fobType !== 'dormakaba') {
      return <FormattedMessage {...messages.cardIdentifierInputHelperTextDriverAvailable} />;
    }
  };

  const getEndAdornment = () => {
    if (isReading) {
      return (
        <Box sx={{ height: 'auto' }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (isValid && cardId) {
      return (
        <Box sx={{ color: 'success.main' }}>
          <CheckCircleIcon />
        </Box>
      );
    }

    if (isError && !isInitError) {
      return (
        <Box sx={{ color: 'error.main' }}>
          <CancelIcon />
        </Box>
      );
    }

    return null;
  };

  const helperText = getHelperText();

  return (
    <OutlinedTextField
      {...textFieldProps}
      color="primary"
      disabled={isReading}
      error={(isError || isInvalid) && !isInitError}
      fullWidth
      helperText={helperText}
      slotProps={{
        ...slotProps,
        input: { ...slotProps?.input, autoFocus: true, endAdornment: getEndAdornment() },
        inputLabel: { ...slotProps?.inputLabel, shrink: true },
      }}
      mask={mask}
      allowedChars={allowedChars}
      inputRef={inputRef}
      label={<FormattedMessage {...messages.cardIdentifierInputLabel} />}
      name={FormFieldNames.Card}
      placeholder={error && !isInitError ? formatMessage(messages.cardIdentifierInputHelperTextErrorPlaceholder) : ''}
      sx={{
        '& .MuiFormHelperText-root': {
          paddingBottom: helperText ? 1 : 0,
        },
      }}
    />
  );
};

export default CardIdentifierInput;
